import React, { useState, useEffect } from 'react';

const LeadsFilter = ({ 
  filters, 
  onFilterChange, 
  onResetFilters, 
  availableTeams = [],
  availableProjects = [],
  availableAgenturen = [],
  availableServiceProviders = [],
  showSearch = true,
  showProjectFilter = true,
  showTeamFilter = true,
  showAgenturFilter = false,
  showDienstleisterFilter = true,
  showDateFilters = true,
  showStatusFilters = true,
  showSortFilters = true,
  customSortOptions = [],
  isMobile = false,
  isTablet = false
}) => {
  const [localFilters, setLocalFilters] = useState(filters);

  // Synchronisiere lokale Filter mit übergebenen Filtern
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (filterName, value) => {
    const newFilters = {
      ...localFilters,
      [filterName]: value
    };
    setLocalFilters(newFilters);
    onFilterChange(filterName, value);
  };

  const clearNameSearch = () => {
    handleFilterChange('search', '');
  };

  const resetFilters = () => {
    const defaultFilters = {
      search: '',
      sortBy: 'erstellt',
      sortOrder: 'desc',
      dateFrom: '',
      dateTo: '',
      statusPv: '',
      statusWp: '',
      dienstleister: '',
      projekt: '',
      teamId: '',
      agenturId: ''
    };
    setLocalFilters(defaultFilters);
    onResetFilters();
  };

  // Responsive Styles
  const styles = {
    container: {
      backgroundColor: '#f8f9fa',
      border: '1px solid #e9ecef',
      borderRadius: '8px',
      padding: isMobile ? '12px' : '16px',
      marginBottom: '16px'
    },
    searchRow: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'stretch' : 'center',
      gap: '12px',
      marginBottom: '16px',
      flexWrap: 'wrap'
    },
    searchGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flex: isMobile ? '1' : '0 0 auto'
    },
    searchGroupFlex: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flex: isMobile ? '1' : '0 0 300px', // Auf Mobile: volle Breite, Desktop: feste Breite
      maxWidth: isMobile ? '100%' : '300px'
    },
    searchInputFlex: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      flex: '1'
    },
    searchInput: {
      flex: '1',
      padding: '6px 10px',
      border: '1px solid #ced4da',
      borderRadius: '4px',
      fontSize: '14px',
      minWidth: '120px'
    },
    clearSearchButton: {
      background: '#dc3545',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      padding: '4px 8px',
      cursor: 'pointer',
      fontSize: '12px',
      fontWeight: 'bold',
      minWidth: '24px',
      height: '28px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    },
    searchResultsInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '14px',
      color: '#6c757d'
    },
    noResults: {
      color: '#dc3545',
      fontWeight: '500'
    },
    resultsCount: {
      color: '#28a745',
      fontWeight: '500'
    },
    filterRow: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: '12px',
      marginBottom: '12px',
      flexWrap: 'wrap'
    },
    filterGroup: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'stretch' : 'center',
      gap: '6px',
      minWidth: isMobile ? '100%' : '150px',
      flex: isMobile ? '1' : '0 0 auto'
    },
    filterLabel: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#495057',
      whiteSpace: 'nowrap',
      minWidth: isMobile ? 'auto' : '80px'
    },
    filterInput: {
      padding: '6px 10px',
      border: '1px solid #ced4da',
      borderRadius: '4px',
      fontSize: '14px',
      backgroundColor: 'white',
      minWidth: isMobile ? '100%' : '120px',
      flex: isMobile ? '1' : '0 0 auto'
    },
    resetButton: {
      background: '#6c757d',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      padding: '8px 16px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      marginTop: isMobile ? '8px' : '0',
      alignSelf: isMobile ? 'stretch' : 'flex-start'
    }
  };

  // Standard Sortier-Optionen
  const defaultSortOptions = [
    { value: 'erstellt', label: 'Erstellt' },
    { value: 'name', label: 'Name' }
  ];

  const sortOptions = customSortOptions.length > 0 ? customSortOptions : defaultSortOptions;

  return (
    <div style={styles.container}>
      {/* Suchzeile */}
      {showSearch && (
        <div style={styles.searchRow}>
          <div style={styles.searchGroupFlex}>
            <label style={styles.filterLabel}>🔍 Suche:</label>
            <div style={styles.searchInputFlex}>
              <input
                type="text"
                placeholder="Name eingeben..."
                value={localFilters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                style={styles.searchInput}
              />
              {localFilters.search && (
                <button 
                  style={styles.clearSearchButton}
                  onClick={clearNameSearch}
                  title="Suche löschen"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          
        </div>
      )}

      {/* Erste Filter-Zeile */}
      <div style={styles.filterRow}>
        {showSortFilters && (
          <>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Sortieren nach:</label>
              <select 
                value={localFilters.sortBy || 'erstellt'} 
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                style={styles.filterInput}
              >
                {sortOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Reihenfolge:</label>
              <select 
                value={localFilters.sortOrder || 'desc'} 
                onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                style={styles.filterInput}
              >
                <option value="desc">Absteigend</option>
                <option value="asc">Aufsteigend</option>
              </select>
            </div>
          </>
        )}

        {showDienstleisterFilter && (
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Dienstleister:</label>
            <select 
              value={localFilters.dienstleister || ''}
              onChange={(e) => handleFilterChange('dienstleister', e.target.value)}
              style={styles.filterInput}
            >
              <option value="">Alle Dienstleister</option>
              {availableServiceProviders.map(provider => (
                <option key={provider} value={provider}>
                  {provider}
                </option>
              ))}
            </select>
          </div>
        )}

        {showProjectFilter && (
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Projekt:</label>
            <select 
              value={localFilters.projekt || ''}
              onChange={(e) => handleFilterChange('projekt', e.target.value)}
              style={styles.filterInput}
            >
              <option value="">Alle Projekte</option>
              {availableProjects.map(project => (
                <option key={project} value={project}>
                  {project}
                </option>
              ))}
            </select>
          </div>
        )}

        {showTeamFilter && (
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Team:</label>
            <select 
              value={localFilters.teamId || ''}
              onChange={(e) => handleFilterChange('teamId', e.target.value)}
              style={styles.filterInput}
            >
              <option value="">Alle Teams</option>
              {availableTeams.map(team => (
                <option key={team} value={team}>
                  {team}
                </option>
              ))}
            </select>
          </div>
        )}

        {showAgenturFilter && (
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>Agentur:</label>
            <select 
              value={localFilters.agenturId || ''}
              onChange={(e) => handleFilterChange('agenturId', e.target.value)}
              style={styles.filterInput}
            >
              <option value="">Alle Agenturen</option>
              {availableAgenturen.map(agentur => (
                <option key={agentur} value={agentur}>
                  {agentur}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      
      {/* Zweite Filter-Zeile */}
      <div style={styles.filterRow}>
        {showDateFilters && (
          <>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Von Datum:</label>
              <input 
                type="date" 
                value={localFilters.dateFrom || ''}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                style={styles.filterInput}
              />
            </div>
            
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Bis Datum:</label>
              <input 
                type="date" 
                value={localFilters.dateTo || ''}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                style={styles.filterInput}
              />
            </div>
          </>
        )}
        
        {showStatusFilters && (
          <>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>PV Status:</label>
              <select 
                value={localFilters.statusPv || ''} 
                onChange={(e) => handleFilterChange('statusPv', e.target.value)}
                style={styles.filterInput}
              >
                <option value="">Alle Status</option>
                <option value="Offen">Offen</option>
                <option value="Qualifiziert">Qualifiziert</option>
                <option value="Kein Interesse mehr">Kein Interesse mehr</option>
                <option value="Widerruf">Widerruf</option>
                <option value="Telefonnummer ungültig">Telefonnummer ungültig</option>
                <option value="Test Lead">Test Lead</option>
                <option value="Kwh zu niedrig">Kwh zu niedrig</option>
                <option value="Denkmalschutz">Denkmalschutz</option>
                <option value="Dach muss saniert werden">Dach muss saniert werden</option>
                <option value="Technisch nicht möglich">Technisch nicht möglich</option>
                <option value="Dublette">Dublette</option>
              </select>
            </div>
            
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>WP Status:</label>
              <select 
                value={localFilters.statusWp || ''} 
                onChange={(e) => handleFilterChange('statusWp', e.target.value)}
                style={styles.filterInput}
              >
                <option value="">Alle Status</option>
                <option value="Offen">Offen</option>
                <option value="Qualifiziert">Qualifiziert</option>
                <option value="Kein Interesse mehr">Kein Interesse mehr</option>
                <option value="Widerruf">Widerruf</option>
                <option value="Telefonnummer ungültig">Telefonnummer ungültig</option>
                <option value="Test Lead">Test Lead</option>
                <option value="Kwh zu niedrig">Kwh zu niedrig</option>
                <option value="Denkmalschutz">Denkmalschutz</option>
                <option value="Dach muss saniert werden">Dach muss saniert werden</option>
                <option value="Technisch nicht möglich">Technisch nicht möglich</option>
                <option value="Dublette">Dublette</option>
              </select>
            </div>
          </>
        )}
        
        <div style={styles.filterGroup}>
          <button style={styles.resetButton} onClick={resetFilters}>
            Alle Filter zurücksetzen
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeadsFilter;
